import "./styles.css";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import type { FaceLandmarkerResult, NormalizedLandmark } from "@mediapipe/tasks-vision";

type Vec2 = { x: number; y: number };
type Pose = { x: number; y: number; width: number; roll: number; yaw: number; pitch: number };
type FrameModel = {
  id: string;
  name: string;
  description: string;
  color: string;
  lens: string;
  front: string;
  leftArm: string;
  rightArm: string;
};

const FACE_OVAL = [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109];
const DEBUG_POINTS = [10, 8, 33, 133, 263, 362, 58, 288, 127, 356, 168, 1];

const frames: FrameModel[] = [
  {
    id: "round",
    name: "Orbit",
    description: "Soft round acetate",
    color: "#1a1d22",
    lens: "rgba(190, 225, 238, .10)",
    leftArm: `<path d="M64 69 9 47"/><path d="M9 47 1 50"/>`,
    rightArm: `<path d="M336 69l55-22"/><path d="M391 47l8 3"/>`,
    front: `<circle cx="112" cy="76" r="58"/><circle cx="288" cy="76" r="58"/><path d="M170 72c18-15 42-15 60 0"/><path class="lens" d="M112 22a54 54 0 1 0 0 108 54 54 0 0 0 0-108Zm176 0a54 54 0 1 0 0 108 54 54 0 0 0 0-108Z"/>`,
  },
  {
    id: "wayfarer",
    name: "Metro",
    description: "Classic angular frame",
    color: "#171719",
    lens: "rgba(190, 225, 238, .08)",
    leftArm: `<path d="M61 59 7 39"/><path d="M7 39 1 43"/>`,
    rightArm: `<path d="M339 59l54-20"/><path d="M393 39l6 4"/>`,
    front: `<path d="M48 30h130l-12 96H67Z"/><path d="M222 30h130l-19 96H234Z"/><path d="M174 57c16-13 36-13 52 0"/><path class="lens" d="M57 39h111l-10 77H75Zm176 0h110l-16 77h-85Z"/>`,
  },
  {
    id: "aviator",
    name: "Aero",
    description: "Lightweight aviator",
    color: "#b5a373",
    lens: "rgba(115, 153, 165, .16)",
    leftArm: `<path d="M64 51 8 35"/><path d="M8 35 1 38"/>`,
    rightArm: `<path d="M336 51l56-16"/><path d="M392 35l7 3"/>`,
    front: `<path d="M44 29c59-12 117 0 132 28 9 18 1 64-26 83-28 20-70 6-87-23-16-29-22-75-19-88Z"/><path d="M356 29c-59-12-117 0-132 28-9 18-1 64 26 83 28 20 70 6 87-23 16-29 22-75 19-88Z"/><path d="M174 51c17-11 35-11 52 0M200 50v78"/><path class="lens" d="M53 37c49-8 97 2 113 25 9 14 0 53-22 69-21 15-54 4-70-20-15-23-23-58-21-74Zm294 0c-49-8-97 2-113 25-9 14 0 53 22 69 21 15 54 4 70-20 15-23 23-58 21-74Z"/>`,
  },
  {
    id: "clear",
    name: "Cloud",
    description: "Translucent crystal",
    color: "rgba(220, 241, 246, .82)",
    lens: "rgba(220, 245, 252, .08)",
    leftArm: `<path d="M61 66 8 49"/><path d="M8 49 1 52"/>`,
    rightArm: `<path d="M339 66l53-17"/><path d="M392 49l7 3"/>`,
    front: `<path d="M48 32c38-13 95-13 130 0l-8 91c-25 19-81 20-107-2Z"/><path d="M352 32c-38-13-95-13-130 0l8 91c25 19 81 20 107-2Z"/><path d="M174 57c17-13 35-13 52 0"/><path class="lens" d="M59 40c34-9 76-9 108 0l-7 76c-24 13-65 14-87-1Zm282 0c-34-9-76-9-108 0l7 76c24 13 65 14 87-1Z"/>`,
  },
];

const $ = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element: ${selector}`);
  return element;
};

const video = $("#camera") as HTMLVideoElement;
const stage = $("#stage");
const emptyState = $("#empty-state");
const liveUi = $("#live-ui");
const startButton = $("#start-button") as HTMLButtonElement;
const stopButton = $("#stop-button") as HTMLButtonElement;
const switchButton = $("#switch-camera") as HTMLButtonElement;
const permissionHelp = $("#permission-help");
const trackingLabel = $("#tracking-label");
const liveBadge = document.querySelector(".live-badge") as HTMLElement;
const glassesBack = $("#glasses-back");
const glassesFront = $("#glasses-front");
const occlusionCanvas = $("#occlusion-canvas") as HTMLCanvasElement;
const debugCanvas = $("#debug-canvas") as HTMLCanvasElement;
const frameList = $("#frame-list");
const performanceLabel = $("#performance");
const settingsButton = $("#settings-button") as HTMLButtonElement;
const settingsPanel = $("#settings-panel");
const settingsBackdrop = $("#settings-backdrop");
const settingsClose = $("#settings-close") as HTMLButtonElement;
const fitRange = $("#fit-range") as HTMLInputElement;
const verticalRange = $("#vertical-range") as HTMLInputElement;
const fitOutput = $("#fit-output") as HTMLOutputElement;
const verticalOutput = $("#vertical-output") as HTMLOutputElement;
const debugToggle = $("#debug-toggle") as HTMLInputElement;
const resetFit = $("#reset-fit") as HTMLButtonElement;

let faceLandmarker: FaceLandmarker | null = null;
let trackerDelegate: "GPU" | "CPU" = "GPU";
let trackerRecoveryAttempted = false;
let lastVideoTime = -1;
let stream: MediaStream | null = null;
let animationId = 0;
let processing = false;
let facingMode: "user" | "environment" = "user";
let selectedFrame = frames[0]!;
let smoothedPose: Pose | null = null;
let lastFaceAt = 0;
let lastResultAt = 0;
let fpsSamples: number[] = [];
let currentLandmarks: NormalizedLandmark[] | null = null;
let localModelBufferPromise: Promise<Uint8Array | null> | null = null;

function svgMarkup(model: FrameModel, layer: "front" | "back", preview = false): string {
  const strokeWidth = model.id === "aviator" ? 5 : model.id === "clear" ? 10 : 9;
  const arms = `${model.leftArm}${model.rightArm}`;
  const content = preview ? `${arms}${model.front}` : layer === "back" ? arms : model.front;
  return `<svg viewBox="0 0 400 155" role="img" aria-label="${model.name} glasses" style="color:${model.color}">
    <g fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">${content}</g>
    <g class="lens-fill" fill="${model.lens}" stroke="none">${layer === "front" || preview ? model.front.match(/<path class="lens"[^>]*d="([^"]+)"\/>/)?.[0] ?? "" : ""}</g>
  </svg>`;
}

function cleanFrontSvg(model: FrameModel): string {
  return model.front.replace(/<path class="lens"[^>]*\/>/, "");
}

function renderSelectedFrame(): void {
  glassesBack.innerHTML = `<svg viewBox="0 0 400 155" aria-hidden="true"><g fill="none" stroke="${selectedFrame.color}" stroke-width="${selectedFrame.id === "aviator" ? 5 : selectedFrame.id === "clear" ? 10 : 9}" stroke-linecap="round" stroke-linejoin="round"><g class="left-arm">${selectedFrame.leftArm}</g><g class="right-arm">${selectedFrame.rightArm}</g></g></svg>`;
  const lensPath = selectedFrame.front.match(/<path class="lens"[^>]*d="([^"]+)"\/>/)?.[1] ?? "";
  glassesFront.innerHTML = `<svg viewBox="0 0 400 155" aria-hidden="true"><path d="${lensPath}" fill="${selectedFrame.lens}"/><g fill="none" stroke="${selectedFrame.color}" stroke-width="${selectedFrame.id === "aviator" ? 5 : selectedFrame.id === "clear" ? 10 : 9}" stroke-linecap="round" stroke-linejoin="round">${cleanFrontSvg(selectedFrame)}</g></svg>`;
}

function renderFrameList(): void {
  frameList.innerHTML = "";
  for (const model of frames) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "frame-card";
    button.setAttribute("role", "radio");
    button.setAttribute("aria-checked", String(model.id === selectedFrame.id));
    button.style.setProperty("--swatch", model.color);
    button.innerHTML = `<span class="frame-preview">${svgMarkup(model, "front", true)}</span><span class="frame-copy"><strong>${model.name}</strong><small>${model.description}</small></span><span class="frame-swatch" aria-hidden="true"></span>`;
    button.addEventListener("click", () => {
      selectedFrame = model;
      renderSelectedFrame();
      renderFrameList();
    });
    frameList.append(button);
  }
}

function openSettings(open: boolean): void {
  settingsPanel.classList.toggle("open", open);
  settingsPanel.setAttribute("aria-hidden", String(!open));
  settingsButton.setAttribute("aria-expanded", String(open));
  settingsBackdrop.hidden = !open;
}

function resizeCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const rect = stage.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(1, Math.round(rect.width * dpr));
  const height = Math.max(1, Math.round(rect.height * dpr));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const context = canvas.getContext("2d");
  if (!context) throw new Error("2D canvas is unavailable");
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  context.clearRect(0, 0, rect.width, rect.height);
  return context;
}

function coverGeometry(): { width: number; height: number; renderedWidth: number; renderedHeight: number; offsetX: number; offsetY: number } {
  const rect = stage.getBoundingClientRect();
  const sourceWidth = video.videoWidth || 1280;
  const sourceHeight = video.videoHeight || 720;
  const scale = Math.max(rect.width / sourceWidth, rect.height / sourceHeight);
  const renderedWidth = sourceWidth * scale;
  const renderedHeight = sourceHeight * scale;
  return {
    width: rect.width,
    height: rect.height,
    renderedWidth,
    renderedHeight,
    offsetX: (rect.width - renderedWidth) / 2,
    offsetY: (rect.height - renderedHeight) / 2,
  };
}

function mapLandmark(point: NormalizedLandmark): Vec2 {
  const geometry = coverGeometry();
  const unmirroredX = geometry.offsetX + point.x * geometry.renderedWidth;
  return {
    x: geometry.width - unmirroredX,
    y: geometry.offsetY + point.y * geometry.renderedHeight,
  };
}

function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function smoothValue(previous: number, next: number, alpha: number): number {
  return previous + (next - previous) * alpha;
}

function smoothAngle(previous: number, next: number, alpha: number): number {
  let delta = next - previous;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return previous + delta * alpha;
}

function calculatePose(landmarks: NormalizedLandmark[]): Pose | null {
  const leftTempleRaw = landmarks[127];
  const rightTempleRaw = landmarks[356];
  const leftEyeRaw = landmarks[33];
  const rightEyeRaw = landmarks[263];
  const foreheadRaw = landmarks[10];
  const glabellaRaw = landmarks[8];
  if (!leftTempleRaw || !rightTempleRaw || !leftEyeRaw || !rightEyeRaw || !foreheadRaw || !glabellaRaw) return null;

  const leftTemple = mapLandmark(leftTempleRaw);
  const rightTemple = mapLandmark(rightTempleRaw);
  const leftEye = mapLandmark(leftEyeRaw);
  const rightEye = mapLandmark(rightEyeRaw);
  const eyeCenter = { x: (leftEye.x + rightEye.x) / 2, y: (leftEye.y + rightEye.y) / 2 };
  const templeWidth = distance(leftTemple, rightTemple);
  const roll = Math.atan2(rightTemple.y - leftTemple.y, rightTemple.x - leftTemple.x);

  const horizontalDepth = leftTempleRaw.z - rightTempleRaw.z;
  const horizontalSpan = Math.max(Math.abs(leftTempleRaw.x - rightTempleRaw.x), .001);
  const yaw = Math.max(-.72, Math.min(.72, Math.atan2(horizontalDepth, horizontalSpan) * 2.1));

  const verticalDepth = foreheadRaw.z - glabellaRaw.z;
  const verticalSpan = Math.max(Math.abs(foreheadRaw.y - glabellaRaw.y), .001);
  const pitch = Math.max(-.28, Math.min(.28, Math.atan2(verticalDepth, verticalSpan) * .35));
  const fit = Number(fitRange.value) / 100;
  const vertical = Number(verticalRange.value) / 100;

  return {
    x: eyeCenter.x,
    y: eyeCenter.y + templeWidth * (.045 + vertical * .45),
    width: templeWidth * 1.2 * fit,
    roll,
    yaw,
    pitch,
  };
}

function applyPose(nextPose: Pose): void {
  const alpha = smoothedPose ? .32 : 1;
  if (!smoothedPose) {
    smoothedPose = { ...nextPose };
  } else {
    smoothedPose.x = smoothValue(smoothedPose.x, nextPose.x, alpha);
    smoothedPose.y = smoothValue(smoothedPose.y, nextPose.y, alpha);
    smoothedPose.width = smoothValue(smoothedPose.width, nextPose.width, alpha);
    smoothedPose.roll = smoothAngle(smoothedPose.roll, nextPose.roll, alpha);
    smoothedPose.yaw = smoothValue(smoothedPose.yaw, nextPose.yaw, alpha);
    smoothedPose.pitch = smoothValue(smoothedPose.pitch, nextPose.pitch, alpha);
  }

  const pose = smoothedPose;
  // The camera preview is mirrored for a natural selfie view, so the CSS Y rotation must be inverted.
  const displayYaw = -pose.yaw;
  const transform = `translate(-50%, -50%) rotateZ(${pose.roll}rad) rotateY(${displayYaw}rad) rotateX(${pose.pitch}rad)`;

  // The SVG artwork is flipped across its horizontal axis in CSS so the bridge and
  // temple-arm curves point downward. This does not alter left/right yaw semantics.

  // In the mirrored selfie view, the SVG side selected by CSS yaw is the inverse of the
  // wearer-facing arm convention. Keep the frame rotation as-is, but swap the arm mapping:
  // a visual left turn reveals the left glasses arm, and a visual right turn reveals the right.
  const turnStrength = Math.min(1, Math.abs(displayYaw) / .42);
  const centeredOpacity = .58;
  // The selected arm must stay visible even when it lies over the mirrored face silhouette.
  // The arm layer is above the occlusion canvas; only the non-selected arm fades away.
  const activeOpacity = 1;
  const inactiveOpacity = Math.max(0, centeredOpacity * (1 - turnStrength * 1.35));
  const leftArm = glassesBack.querySelector<SVGGElement>(".left-arm");
  const rightArm = glassesBack.querySelector<SVGGElement>(".right-arm");
  if (leftArm && rightArm) {
    if (Math.abs(displayYaw) < .035) {
      glassesBack.dataset.visibleArm = "both";
      leftArm.style.opacity = String(centeredOpacity);
      rightArm.style.opacity = String(centeredOpacity);
    } else {
      // Preserve the v2.0.4 anatomical mapping; only fix visibility/layering here.
      const showLeftArm = displayYaw > 0;
      glassesBack.dataset.visibleArm = showLeftArm ? "left" : "right";
      leftArm.style.opacity = String(showLeftArm ? activeOpacity : inactiveOpacity);
      rightArm.style.opacity = String(showLeftArm ? inactiveOpacity : activeOpacity);
    }
  }

  for (const layer of [glassesBack, glassesFront]) {
    layer.style.left = `${pose.x}px`;
    layer.style.top = `${pose.y}px`;
    layer.style.width = `${pose.width}px`;
    layer.style.transform = transform;
    layer.classList.add("tracked");
  }
}

function drawOcclusion(landmarks: NormalizedLandmark[]): void {
  const context = resizeCanvas(occlusionCanvas);
  const geometry = coverGeometry();
  const points = FACE_OVAL.map((index) => landmarks[index]).filter((point): point is NormalizedLandmark => Boolean(point)).map(mapLandmark);
  if (points.length < 3) return;

  context.save();
  context.beginPath();
  context.moveTo(points[0]!.x, points[0]!.y);
  for (const point of points.slice(1)) context.lineTo(point.x, point.y);
  context.closePath();
  context.clip();
  context.translate(geometry.width, 0);
  context.scale(-1, 1);
  context.drawImage(video, geometry.offsetX, geometry.offsetY, geometry.renderedWidth, geometry.renderedHeight);
  context.restore();
}

function drawDebug(landmarks: NormalizedLandmark[] | null): void {
  const context = resizeCanvas(debugCanvas);
  if (!debugToggle.checked || !landmarks) return;
  context.fillStyle = "rgba(94, 225, 200, .95)";
  context.strokeStyle = "rgba(94, 225, 200, .55)";
  context.lineWidth = 1;
  for (const index of DEBUG_POINTS) {
    const landmark = landmarks[index];
    if (!landmark) continue;
    const point = mapLandmark(landmark);
    context.beginPath();
    context.arc(point.x, point.y, 3, 0, Math.PI * 2);
    context.fill();
  }
  const oval = FACE_OVAL.map((index) => landmarks[index]).filter((point): point is NormalizedLandmark => Boolean(point)).map(mapLandmark);
  if (oval.length > 2) {
    context.beginPath();
    context.moveTo(oval[0]!.x, oval[0]!.y);
    for (const point of oval.slice(1)) context.lineTo(point.x, point.y);
    context.closePath();
    context.stroke();
  }
}

function hideGlasses(): void {
  glassesBack.classList.remove("tracked");
  glassesFront.classList.remove("tracked");
  resizeCanvas(occlusionCanvas);
  drawDebug(null);
  liveBadge.classList.remove("is-tracking");
  trackingLabel.textContent = "Looking for a face";
}

function updatePerformance(timestamp: number): void {
  if (lastResultAt > 0) {
    const delta = timestamp - lastResultAt;
    if (delta > 0) {
      fpsSamples.push(1000 / delta);
      if (fpsSamples.length > 20) fpsSamples.shift();
      const average = fpsSamples.reduce((sum, value) => sum + value, 0) / fpsSamples.length;
      performanceLabel.textContent = `${Math.round(average)} fps`;
    }
  }
  lastResultAt = timestamp;
}

function onResults(results: FaceLandmarkerResult): void {
  const now = performance.now();
  updatePerformance(now);
  const landmarks = results.faceLandmarks?.[0] ?? null;
  currentLandmarks = landmarks;
  if (!landmarks) {
    if (now - lastFaceAt > 180) hideGlasses();
    return;
  }

  const pose = calculatePose(landmarks);
  if (!pose) return;
  lastFaceAt = now;
  applyPose(pose);
  drawOcclusion(landmarks);
  drawDebug(landmarks);
  liveBadge.classList.add("is-tracking");
  trackingLabel.textContent = "Tracking your face";
}

const REMOTE_MODEL_URL = "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

function assetUrl(path: string): string {
  return new URL(path, document.baseURI).toString();
}

async function loadLocalModelBuffer(): Promise<Uint8Array | null> {
  if (localModelBufferPromise) return localModelBufferPromise;
  localModelBufferPromise = (async () => {
    const localModel = assetUrl("./mediapipe/face_landmarker.task");
    try {
      const response = await fetch(localModel, { cache: "force-cache" });
      if (!response.ok) return null;
      const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
      if (contentType.includes("text/html")) return null;
      const buffer = new Uint8Array(await response.arrayBuffer());
      return buffer.byteLength > 1_000_000 ? buffer : null;
    } catch {
      return null;
    }
  })();
  return localModelBufferPromise;
}

async function createTracker(delegate: "GPU" | "CPU"): Promise<FaceLandmarker> {
  const vision = await FilesetResolver.forVisionTasks(assetUrl("./mediapipe/wasm"));
  const modelAssetBuffer = await loadLocalModelBuffer();
  const baseOptions = modelAssetBuffer
    ? { modelAssetBuffer, delegate }
    : { modelAssetPath: REMOTE_MODEL_URL, delegate };
  return FaceLandmarker.createFromOptions(vision, {
    baseOptions,
    runningMode: "VIDEO",
    numFaces: 1,
    minFaceDetectionConfidence: .5,
    minFacePresenceConfidence: .5,
    minTrackingConfidence: .5,
    outputFaceBlendshapes: false,
    outputFacialTransformationMatrixes: false,
  });
}

async function ensureTracker(): Promise<void> {
  if (faceLandmarker) return;
  trackingLabel.textContent = "Loading face tracker";
  try {
    trackerDelegate = "GPU";
    faceLandmarker = await createTracker("GPU");
  } catch (gpuError) {
    console.warn("GPU face tracker unavailable; using CPU", gpuError);
    trackerDelegate = "CPU";
    faceLandmarker = await createTracker("CPU");
  }
  trackerRecoveryAttempted = trackerDelegate === "CPU";
  trackingLabel.textContent = "Looking for a face";
}

async function recoverTracker(error: unknown): Promise<boolean> {
  if (trackerDelegate !== "GPU" || trackerRecoveryAttempted) return false;
  trackerRecoveryAttempted = true;
  console.warn("GPU inference failed; rebuilding face tracker on CPU", error);
  trackingLabel.textContent = "Switching to compatible tracking";
  try {
    faceLandmarker?.close();
  } catch {
    // Ignore cleanup errors from the failed delegate.
  }
  faceLandmarker = null;
  trackerDelegate = "CPU";
  faceLandmarker = await createTracker("CPU");
  trackingLabel.textContent = "Looking for a face";
  return true;
}

function formatTrackingError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error);
  return raw.replace(/\s+/g, " ").trim().slice(0, 180) || "Unknown inference error";
}

async function processFrame(): Promise<void> {
  if (!stream || !faceLandmarker) return;
  if (!processing && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.currentTime !== lastVideoTime) {
    processing = true;
    const timestamp = performance.now();
    lastVideoTime = video.currentTime;
    try {
      const results = faceLandmarker.detectForVideo(video, timestamp);
      onResults(results);
    } catch (error) {
      try {
        const recovered = await recoverTracker(error);
        if (!recovered) throw error;
      } catch (finalError) {
        console.error("Face tracking failed", finalError);
        hideGlasses();
        trackingLabel.textContent = `Tracking error: ${formatTrackingError(finalError)}`;
      }
    } finally {
      processing = false;
    }
  }
  animationId = requestAnimationFrame(() => void processFrame());
}

function cameraConstraints(): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      facingMode: { ideal: facingMode },
      width: { ideal: 1280 },
      height: { ideal: 960 },
      frameRate: { ideal: 30, max: 60 },
    },
  };
}

async function startCamera(): Promise<void> {
  if (!navigator.mediaDevices?.getUserMedia) {
    permissionHelp.hidden = false;
    permissionHelp.textContent = "Camera access is unavailable. Use HTTPS, localhost, or the Android app.";
    return;
  }

  startButton.disabled = true;
  startButton.lastChild!.textContent = " Loading…";
  permissionHelp.hidden = true;
  try {
    await ensureTracker();
    stream?.getTracks().forEach((track) => track.stop());
    stream = await navigator.mediaDevices.getUserMedia(cameraConstraints());
    video.srcObject = stream;
    await video.play();
    emptyState.hidden = true;
    liveUi.hidden = false;
    cancelAnimationFrame(animationId);
    animationId = requestAnimationFrame(() => void processFrame());
  } catch (error) {
    console.error(error);
    const name = error instanceof DOMException ? error.name : "";
    permissionHelp.hidden = false;
    permissionHelp.textContent = name === "NotAllowedError"
      ? "Camera permission was denied. Allow camera access in your browser or Android app settings, then try again."
      : `The camera or face tracker could not start: ${formatTrackingError(error)}`;
  } finally {
    startButton.disabled = false;
    startButton.lastChild!.textContent = " Enable camera";
  }
}

function stopCamera(): void {
  cancelAnimationFrame(animationId);
  stream?.getTracks().forEach((track) => track.stop());
  stream = null;
  video.srcObject = null;
  processing = false;
  lastVideoTime = -1;
  smoothedPose = null;
  currentLandmarks = null;
  fpsSamples = [];
  performanceLabel.textContent = "";
  hideGlasses();
  liveUi.hidden = true;
  emptyState.hidden = false;
}

async function switchCamera(): Promise<void> {
  facingMode = facingMode === "user" ? "environment" : "user";
  await startCamera();
}

function updateCalibrationLabels(): void {
  fitOutput.value = `${fitRange.value}%`;
  verticalOutput.value = Number(verticalRange.value) > 0 ? `+${verticalRange.value}` : verticalRange.value;
  if (currentLandmarks) {
    const pose = calculatePose(currentLandmarks);
    if (pose) applyPose(pose);
  }
}

startButton.addEventListener("click", () => void startCamera());
stopButton.addEventListener("click", stopCamera);
switchButton.addEventListener("click", () => void switchCamera());
settingsButton.addEventListener("click", () => openSettings(!settingsPanel.classList.contains("open")));
settingsClose.addEventListener("click", () => openSettings(false));
settingsBackdrop.addEventListener("click", () => openSettings(false));
fitRange.addEventListener("input", updateCalibrationLabels);
verticalRange.addEventListener("input", updateCalibrationLabels);
debugToggle.addEventListener("change", () => drawDebug(currentLandmarks));
resetFit.addEventListener("click", () => {
  fitRange.value = "100";
  verticalRange.value = "0";
  debugToggle.checked = false;
  updateCalibrationLabels();
  drawDebug(null);
});
window.addEventListener("resize", () => {
  if (currentLandmarks) {
    const pose = calculatePose(currentLandmarks);
    if (pose) applyPose(pose);
    drawOcclusion(currentLandmarks);
    drawDebug(currentLandmarks);
  }
});
window.addEventListener("pagehide", stopCamera);
document.addEventListener("visibilitychange", () => {
  if (document.hidden && stream) stopCamera();
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") openSettings(false);
});

renderSelectedFrame();
renderFrameList();
updateCalibrationLabels();

if ("serviceWorker" in navigator && location.protocol.startsWith("http")) {
  window.addEventListener("load", () => {
    if (import.meta.env.DEV) {
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
        .then(() => caches.keys())
        .then((keys) => Promise.all(keys.filter((key) => key.startsWith("virtual-glasses-")).map((key) => caches.delete(key))))
        .catch((error) => console.warn("Could not clear an older development service worker", error));
      return;
    }
    navigator.serviceWorker.register("./service-worker.js", { updateViaCache: "none" })
      .then((registration) => registration.update())
      .catch((error) => console.warn("Service worker unavailable", error));
  });
}
